/* eslint-disable react/no-array-index-key */
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import {
  useCallback,
  useMemo,
} from 'react'
import { connect, useDispatch } from 'react-redux'

import { setCategoryAdUnitInitialized } from '../../../../flux/actions/adv'
import {
  addExpandedCategory, removeExpandedCategory,
  setCurrentCollectionIndex,
  uxEvent,
} from '../../../../flux/actions/ux'
import { selectFlyerId } from '../../../../flux/selectors/campaign'
import { selectLabel, selectObjectLabel } from '../../../../flux/selectors/i18n'
import { selectBrandColor, selectBrandColorText } from '../../../../flux/selectors/layout'
import { selectFilteredGroupedProducts } from '../../../../flux/selectors/products'
import {
  selectCountry, selectExpandedCategories,
  selectIsWebView, selectSearch,
} from '../../../../flux/selectors/ux'
import { CLIENT_TYPES } from '../../../../helpers/const'
import withMemo from '../../../../utils/withMemo'

import LoadingSkeleton from '../../Generics/LoadingSkeleton/index'
import SearchEmptyState from '../../SearchEmptyState'
import ProductsCategoryBlock from './components/categoryBlock'
import ProductsGridSkelton from './skelton'

const StyledGrid = styled.div(({ theme: { spaces, breakpoints } }) => ({
  marginBottom: spaces.default,
  display: 'grid',
  gridTemplateColumns: `repeat(2, calc(50% - ${spaces.default / 2}px))`,
  gridGap: `${spaces.default * 0.625}px ${spaces.default}px`,
  [`@media (min-width: ${breakpoints.small})`]: {
    gridGap: `${spaces.default}px ${spaces.default * 1.5}px`,
    gridTemplateColumns: `repeat(4, calc(25% - ${spaces.default * 1.125}px))`,
  },
  [`@media (min-width: ${breakpoints.xlarge})`]: {
    gridTemplateColumns: `repeat(6, calc(16.666% - ${spaces.default * 1.25}px))`,
  },
}))

const ProductsGridCategorized = ({
  groupedProducts,
  productSearch,
  emptyTitle,
  emptyDescription,
  otherDealsLabel,
  productsExpandLabels,
  brandColorBg,
  brandColorText,
  expandedCategories,
  flyerId,
  isWebView,
  country,
}) => {
  const dispatch = useDispatch()

  const {
    show: expandProductsLabel,
    hide: collapseProductsLabel,
  } = productsExpandLabels

  const hasOnlyOtherDealsCategory = useMemo(() => (
    groupedProducts.length === 1 && groupedProducts[0]?.[0] === otherDealsLabel
  ), [groupedProducts])

  const clientType = isWebView ? CLIENT_TYPES.app : CLIENT_TYPES.web

  const onProductClick = useCallback((product, category) => {
    const { collectionIndex, id: productId } = product

    dispatch(setCurrentCollectionIndex(collectionIndex))

    dispatch(uxEvent({
      name: 'click',
      target: 'ProductsGridCategorized/Item',
      params: {
        productId,
        category,
        position: collectionIndex + 1,
      },
    }))
  }, [])

  const onCategoryEnterScreen = slotData => {
    dispatch(setCategoryAdUnitInitialized(slotData))
  }

  const onProductVisible = useCallback((product, category) => {
    const { collectionIndex, id: productId } = product

    dispatch(uxEvent({
      name: 'visible',
      target: 'ProductsGridCategorized/Item',
      params: {
        productId,
        category,
        position: collectionIndex + 1,
      },
    }))
  }, [])

  const onExpandProductsClick = useCallback((category, expand) => {
    if (expand) {
      dispatch(addExpandedCategory(category))
    } else {
      dispatch(removeExpandedCategory(category))
    }
  }, [])

  // groupedProducts[0] -> category
  // groupedProducts[1][1...N] -> products
  if (groupedProducts.length) {
    return groupedProducts.map(([category, products], index) => (
      <ProductsCategoryBlock
        key={`category_${category}_${index}`}
        category={category}
        hasOnlyOtherDealsCategory={hasOnlyOtherDealsCategory}
        products={products}
        isCategoryExpanded={expandedCategories.indexOf(category) >= 0}
        expandProductsLabel={expandProductsLabel}
        collapseProductsLabel={collapseProductsLabel}
        brandColorBg={brandColorBg}
        brandColorText={brandColorText}
        flyerId={flyerId}
        clientType={clientType}
        onProductClick={onProductClick}
        onProductVisible={onProductVisible}
        onFirstEnterScreen={onCategoryEnterScreen}
        onExpandProductsClick={onExpandProductsClick}
        country={country}
      />
    ))
  }

  if (productSearch && !groupedProducts.length) {
    return (
      <SearchEmptyState
        title={emptyTitle}
        description={emptyDescription}
      />
    )
  }

  return (
    <>
      <LoadingSkeleton
        height="min(20vw, 150px)"
        width="100%"
        margin="0 0 10px 0"
      />
      <StyledGrid>
        <ProductsGridSkelton />
      </StyledGrid>
    </>
  )
}

ProductsGridCategorized.propTypes = {
  groupedProducts: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
    PropTypes.array,
  ]),
  productSearch: PropTypes.string,
  emptyTitle: PropTypes.string,
  emptyDescription: PropTypes.string,
  otherDealsLabel: PropTypes.string,
  productsExpandLabels: PropTypes.shape({
    show: PropTypes.string,
    hide: PropTypes.string,
  }),
  brandColorBg: PropTypes.string,
  brandColorText: PropTypes.string,
  expandedCategories: PropTypes.arrayOf(PropTypes.string),
  flyerId: PropTypes.string,
  isWebView: PropTypes.bool,
  country: PropTypes.string,
}

ProductsGridCategorized.defaultProps = {
  groupedProducts: [],
  emptyTitle: '',
  emptyDescription: '',
  otherDealsLabel: '',
  productsExpandLabels: {
    show: '',
    hide: '',
  },
  productSearch: '',
  brandColorBg: '',
  brandColorText: '',
  expandedCategories: [],
  flyerId: '',
  isWebView: false,
  country: '',
}

const mapStateToProps = state => {
  const groupedProducts = selectFilteredGroupedProducts(state)
  return {
    groupedProducts,
    productSearch: selectSearch(state),
    emptyTitle: selectLabel('emptyStateTitle')(state),
    emptyDescription: selectLabel('emptyStateBody')(state),
    otherDealsLabel: selectLabel('otherDeals')(state),
    productsExpandLabels: selectObjectLabel('productsExpand')(state),
    brandColorBg: selectBrandColor(state),
    brandColorText: selectBrandColorText(state),
    expandedCategories: selectExpandedCategories(state),
    flyerId: selectFlyerId(state),
    isWebView: selectIsWebView(state),
    country: selectCountry(state),
  }
}

ProductsGridCategorized.displayName = 'ProductsGridCategorized'
ProductsGridCategorized.whyDidYouRender = true

export default withMemo(
  connect(mapStateToProps)(ProductsGridCategorized),
  [],
)
